/*
 * Offline store — IndexedDB via Dexie.
 *
 * v1: read-only trip snapshot cache (trip, today, documents).
 * v2: mutation queue for write-while-offline support.
 *     Each pending mutation is stored as a MutationRecord and replayed
 *     sequentially when the device comes back online.
 *
 * All functions are safe no-ops when IndexedDB is unavailable (e.g. jsdom).
 */
import Dexie, { type Table } from 'dexie'
import type { Document, TodayData, Trip, TripPoint } from '../api/types'

// ---------------------------------------------------------------------------
// Snapshot (read cache) — unchanged from v1

export interface TripSnapshot {
  tripId: string
  trip: Trip
  today: TodayData | null
  documents: Document[]
  points?: TripPoint[]
  cachedAt: number
}

// ---------------------------------------------------------------------------
// Mutation queue — new in v2

export type MutationMethod = 'POST' | 'PATCH' | 'DELETE' | 'PUT'

export interface MutationRecord {
  /** Auto-incremented primary key — also defines replay order. */
  id?: number
  tripId: string
  method: MutationMethod
  /** Relative path, e.g. "/trips/abc/points" */
  url: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  body: any | null
  createdAt: number
  /** Optimistic client-side ID so UI can correlate the queued item. */
  optimisticId?: string
}

export interface CaptureRecord {
  id: string
  tripId: string
  kind: 'photo' | 'video' | 'voice' | 'note' | 'document'
  pointId: string | null
  blob?: Blob
  fileName?: string
  text?: string
  createdAt: number
}

class OfflineDb extends Dexie {
  snapshots!: Table<TripSnapshot, string>
  mutations!: Table<MutationRecord, number>
  captures!: Table<CaptureRecord, string>

  constructor() {
    super('smart-travel-offline')
    this.version(1).stores({ snapshots: 'tripId' })
    this.version(2).stores({
      snapshots: 'tripId',
      mutations: '++id, tripId, createdAt',
    })
    this.version(3).stores({
      snapshots: 'tripId', mutations: '++id, tripId, createdAt',
      captures: 'id, tripId, createdAt',
    })
  }
}

export async function saveCapture(record: CaptureRecord): Promise<void> {
  const database = getDb()
  if (!database) throw new Error('Offline storage is unavailable')
  await database.captures.put(record)
}

export async function pendingCaptures(tripId: string): Promise<CaptureRecord[]> {
  const database = getDb()
  if (!database) return []
  return database.captures.where('tripId').equals(tripId).sortBy('createdAt')
}

export async function removeCapture(id: string): Promise<void> {
  const database = getDb()
  if (!database) throw new Error('Offline storage is unavailable')
  await database.captures.delete(id)
}

let db: OfflineDb | null = null

function getDb(): OfflineDb | null {
  if (typeof indexedDB === 'undefined') return null
  if (!db) {
    try {
      db = new OfflineDb()
    } catch {
      return null
    }
  }
  return db
}

// ---------------------------------------------------------------------------
// Snapshot helpers (unchanged API)

export async function cacheTripSnapshot(
  snapshot: Omit<TripSnapshot, 'cachedAt'>,
): Promise<void> {
  const database = getDb()
  if (!database) return
  try {
    await database.snapshots.put({ ...snapshot, cachedAt: Date.now() })
  } catch {
    /* ignore */
  }
}

export async function readTripSnapshot(tripId: string): Promise<TripSnapshot | null> {
  const database = getDb()
  if (!database) return null
  try {
    const snap = await database.snapshots.get(tripId)
    if (!snap) return null
    return snap
  } catch {
    return null
  }
}

// ---------------------------------------------------------------------------
// Mutation queue helpers

export async function enqueueMutation(
  record: Omit<MutationRecord, 'id' | 'createdAt'>,
): Promise<number | null> {
  const database = getDb()
  if (!database) return null
  try {
    const id = await database.mutations.add({ ...record, createdAt: Date.now() })
    return id as number
  } catch {
    return null
  }
}

export async function peekMutations(tripId?: string): Promise<MutationRecord[]> {
  const database = getDb()
  if (!database) return []
  try {
    const all = tripId
      ? await database.mutations.where('tripId').equals(tripId).sortBy('id')
      : await database.mutations.orderBy('id').toArray()
    return all
  } catch {
    return []
  }
}

export async function deleteMutation(id: number): Promise<void> {
  const database = getDb()
  if (!database) return
  try {
    await database.mutations.delete(id)
  } catch {
    /* ignore */
  }
}

export async function clearMutations(tripId?: string): Promise<void> {
  const database = getDb()
  if (!database) return
  try {
    if (tripId) {
      await database.mutations.where('tripId').equals(tripId).delete()
    } else {
      await database.mutations.clear()
    }
  } catch {
    /* ignore */
  }
}

export async function mutationQueueSize(tripId?: string): Promise<number> {
  const database = getDb()
  if (!database) return 0
  try {
    return tripId
      ? await database.mutations.where('tripId').equals(tripId).count()
      : await database.mutations.count()
  } catch {
    return 0
  }
}
